import { Suspense, useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { PerformanceMonitor } from '@react-three/drei'
import { EffectComposer, Bloom, ChromaticAberration, Vignette } from '@react-three/postprocessing'
import { BlendFunction } from 'postprocessing'
import * as THREE from 'three'
import AmbientParticles from './AmbientParticles'
import CausticPlane from './CausticPlane'

// ── Camera gentle sway ─────────────────────────────────────────────────────
function CameraSway() {
  const { camera, pointer } = useThree()
  useFrame(() => {
    camera.position.x += (pointer.x * 1.5 - camera.position.x) * 0.02
    camera.position.y += (pointer.y * 0.8 - camera.position.y) * 0.02
  })
  return null
}

// ── Inner scene (needs Canvas context) ────────────────────────────────────
function Scene({ scrollRef }: { scrollRef: React.MutableRefObject<number> }) {
  return (
    <>
      <CameraSway />

      {/* ── Scene objects ── */}
      <Suspense fallback={null}>
        <CausticPlane />
        <AmbientParticles scrollRef={scrollRef} />
      </Suspense>

      {/* ── Post-processing ── */}
      <EffectComposer>
        <Bloom
          luminanceThreshold={0.55}
          luminanceSmoothing={0.9}
          intensity={1.2}
          mipmapBlur
        />
        <ChromaticAberration
          blendFunction={BlendFunction.NORMAL}
          offset={new THREE.Vector2(0.0006, 0.0006)}
          radialModulation={true}
          modulationOffset={0.4}
        />
        <Vignette eskil={false} offset={0.25} darkness={0.85} />
      </EffectComposer>
    </>
  )
}

// ── Exported canvas (fixed full-viewport background) ──────────────────────
export default function LeviathanCanvas({
  scrollRef,
}: {
  scrollRef: React.MutableRefObject<number>
}) {
  const dprRef = useRef<[number, number]>([1, 1.5])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <Canvas
        dpr={dprRef.current}
        camera={{ fov: 50, near: 0.1, far: 200, position: [0, 0, 20] }}
        gl={{
          antialias: false,
          alpha: true,
          powerPreference: 'high-performance',
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.1,
        }}
      >
        <PerformanceMonitor
          onDecline={() => { dprRef.current = [0.75, 1] }}
          onIncline={() => { dprRef.current = [1, 1.5] }}
        />
        <Scene scrollRef={scrollRef} />
      </Canvas>
    </div>
  )
}
